import { useContext } from 'react';

import AJAX from 'common/AJAX';
import {
  type EncodedQuery as InboxEncodedQuery,
  getEncodedQueryParams as getInboxEncodedQueryParams,
  getQueryParams as getInboxQueryParams,
} from 'common/containers/InboxItemListContainer';
import {
  type EncodedQuery as QueueEncodedQuery,
  getPreferencesFromQueryParams,
  getEncodedQueryParams as getQueueEncodedQueryParams,
  getQueryParams as getQueueQueryParams,
} from 'common/containers/QueueItemListContainer';
import { RouterContext } from 'common/containers/RouterContainer';

import type { QueryParams as InboxQueryParams } from 'common/actions/inboxItemQueries';
import type { QueryParams as QueueQueryParams } from 'common/actions/queueItemQueries';
import type { RouterState } from 'react-router';

type UnifiedEncodedQuery = QueueEncodedQuery & InboxEncodedQuery;
type UnifiedQueryParams = QueueQueryParams & InboxQueryParams;

type Props = {
  reloadCompany: () => void;
};

const getQueryParams = ({
  pathname,
  query,
}: {
  pathname: string;
  query: UnifiedEncodedQuery;
}): UnifiedQueryParams => {
  const { sort, ...strippedQuery } = query;
  // we need to extract the sort from the encoded query and add it back to the query params
  // to avoid type errors since it has different types between queue and inbox

  return {
    ...getQueueQueryParams({ pathname, query: strippedQuery }),
    ...getInboxQueryParams({ pathname, query: strippedQuery }),
    sort: sort ?? undefined,
  };
};

const getEncodedUnifiedQueryParams = (queryParams: UnifiedQueryParams): UnifiedEncodedQuery => {
  // we need to extract the sort from the query params and add it back to the encoded query
  // to avoid type errors since it has different types between queue and inbox
  const { sort, ...strippedQueryParams } = queryParams;

  return {
    ...getQueueEncodedQueryParams(strippedQueryParams),
    ...getInboxEncodedQueryParams(strippedQueryParams),
    sort: sort ?? undefined,
  };
};

const useFilterControls = ({ reloadCompany }: Props) => {
  const router = useContext(RouterContext);

  // react-router is typed incorrectly, workaround
  const { pathname, query } = (router as any).location as RouterState['location'];
  const queryParams = getQueryParams({ pathname, query });

  const updateQueuePreferences = async (params: UnifiedQueryParams) => {
    await AJAX.post('/api/viewer/updatePreferences', {
      preferences: {
        queueFilters: getPreferencesFromQueryParams(params),
      },
    });
    reloadCompany();
  };

  const clearFilters = () => {
    updateQueuePreferences({});

    router.replace({
      pathname,
      query: getEncodedUnifiedQueryParams({}),
    });
  };

  const updateFilters = (newParams: Partial<UnifiedQueryParams>) => {
    const updatedParams = { ...queryParams, ...newParams };
    updateQueuePreferences(updatedParams);

    router.replace({
      pathname,
      query: getEncodedUnifiedQueryParams(updatedParams),
    });
  };

  return {
    clearFilters,
    queryParams,
    updateFilters,
  };
};

export default useFilterControls;
